import { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import CategoryIcon from '@material-ui/icons/Category';
import QueueIcon from '@material-ui/icons/Queue';
import ViewListIcon from '@material-ui/icons/ViewList';
import { ARCHITECTURE_PATH } from '../home/HomePage';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

// «Системы» и «Компоненты» ведут на один путь /catalog и различаются только фильтром в query, а меню
// сравнивает путь без query и подсвечивало бы оба пункта. Подсвечиваем пункт по filters[kind].
const CatalogKindItems = () => {
  const { search } = useLocation();
  const kind = new URLSearchParams(search).get('filters[kind]');
  return (
    <>
      <SidebarItem
        icon={CategoryIcon}
        to="catalog?filters[kind]=system"
        text="Системы"
        {...(kind === 'system' ? {} : { activeClassName: '' })}
      />
      <SidebarItem
        icon={ViewListIcon}
        to="catalog?filters[kind]=component"
        text="Компоненты"
        {...(kind === 'component' ? {} : { activeClassName: '' })}
      />
    </>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Главная" />
        <SidebarItem icon={AccountTreeIcon} to={ARCHITECTURE_PATH} text="Архитектура" />
        <SidebarItem icon={QueueIcon} to="queues" text="Очереди" />
        <CatalogKindItems />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="API" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Документация" />
        <MyGroupsSidebarItem
          singularTitle="Моя команда"
          pluralTitle="Мои команды"
          icon={GroupIcon}
        />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
